import { Music2, Pause, Play } from "lucide-react";

import { cn } from "@/lib/utils";

export const FORMATS = [
  { v: "9:16", label: "Vertical", box: "h-6 w-[14px]" },
  { v: "1:1", label: "Square", box: "h-5 w-5" },
  { v: "16:9", label: "Landscape", box: "h-[14px] w-6" },
];
export const MOODS = [
  { v: "upbeat", label: "Upbeat" },
  { v: "luxury", label: "Luxury" },
  { v: "modern", label: "Modern" },
];

export function FormatPicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Format">
      {FORMATS.map((f) => {
        const on = value === f.v;
        return (
          <button key={f.v} type="button" role="radio" aria-checked={on} onClick={() => onChange(f.v)}
            data-testid={`format-option-${f.v.replace(":", "x")}`}
            className={cn("focus-ring flex min-h-[96px] flex-col items-center justify-center gap-1.5 rounded-xl border transition-all",
              on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:border-primary/50")}>
            <span className={cn("rounded-[3px] border-2", f.box, on ? "border-primary-foreground" : "border-secondary-foreground")} aria-hidden />
            <span className="text-base font-semibold">{f.v}</span>
            <span className={cn("text-xs", on ? "text-primary-foreground" : "text-muted-foreground")}>{f.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function PlayButton({ playing, onClick, label, ...p }) {
  return (
    <button type="button" onClick={onClick} aria-label={`${playing ? "Pause" : "Play"} ${label}`}
      className="focus-ring flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform hover:scale-105" {...p}>
      {playing ? <Pause className="h-4 w-4 fill-current" aria-hidden /> : <Play className="ml-0.5 h-4 w-4 fill-current" aria-hidden />}
    </button>
  );
}

export function Chip({ label, selected, onClick, icon, ...p }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={selected}
      className={cn("focus-ring inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full border px-5 text-sm font-medium transition-colors",
        selected ? "border-primary bg-primary/15 text-primary" : "border-input bg-card text-secondary-foreground hover:border-primary/50")} {...p}>
      {icon ? <Music2 className="h-4 w-4" aria-hidden /> : null}
      {label}
    </button>
  );
}

export function VoicePicker({ voices, selected, onSelect, preview }) {
  return (
    <div className="-mx-4 flex snap-x gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 2xl:grid-cols-3"
      data-testid="voice-list">
      {voices.slice(0, 16).map((v) => {
        const on = selected === v.voice_id;
        return (
          <div key={v.voice_id} className={cn("flex w-[200px] shrink-0 snap-start items-center gap-2 rounded-xl border p-2 pl-3 transition-colors sm:w-auto",
            on ? "border-primary bg-primary/15" : "border-border bg-card hover:border-primary/40")}>
            <button type="button" aria-pressed={on} onClick={() => onSelect(v.voice_id)} data-testid={`voice-option-${v.voice_id}`}
              className="focus-ring flex min-h-12 min-w-0 flex-1 flex-col items-start justify-center rounded-md text-left">
              <span className="w-full truncate text-sm font-medium">{v.name.split(" - ")[0]}</span>
              <span className="w-full truncate text-[13px] capitalize text-muted-foreground">{[v.gender, v.accent].filter(Boolean).join(" · ") || v.category}</span>
            </button>
            <PlayButton playing={preview.playing === v.voice_id} label={`${v.name.split(" - ")[0]} voice preview`}
              onClick={() => preview.toggle(v.voice_id, v.preview_url)} data-testid={`voice-preview-${v.voice_id}`} />
          </div>
        );
      })}
    </div>
  );
}
